import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  SlidersHorizontal, ArrowLeft, Search, Loader2, History, Warehouse,
  TrendingUp, TrendingDown, Lightbulb, User, X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface Produto { id: number; codigo: string; descricao: string; unidade: string; estoque_atual: string | number; preco_custo: number }
interface Deposito { id: number; nome: string; is_default: boolean; ativo: boolean }
interface ContaGerencial { id: number; codigo: string; nome: string; tipo: string }

const MOTIVOS: { v: string; label: string }[] = [
  { v: 'contagem', label: 'Contagem / inventário' },
  { v: 'perda', label: 'Perda' },
  { v: 'quebra', label: 'Quebra / avaria' },
  { v: 'correcao', label: 'Correção de cadastro' },
  { v: 'sobra', label: 'Sobra encontrada' },
  { v: 'outro', label: 'Outro' },
]
const motivoLabel = (v?: string | null) => MOTIVOS.find(m => m.v === v)?.label || v || '—'

export default function AjusteEstoquePage() {
  const nav = useNavigate()
  const [tab, setTab] = useState<'novo' | 'historico'>('novo')
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900 flex items-center gap-2">
            <SlidersHorizontal size={22} /> Ajuste de Estoque
          </h1>
          <p className="text-sm text-ink-500 mt-0.5">
            Corrige o saldo por contagem ou entrada/saída avulsa. Registra quem fez, o motivo e a conta gerencial.
          </p>
        </div>
        <button onClick={() => nav('/estoque')} className="btn-ghost"><ArrowLeft size={14} /> Voltar</button>
      </div>

      <div className="flex gap-1 border-b border-ink-200">
        {[{ id: 'novo', label: 'Novo ajuste', icon: SlidersHorizontal }, { id: 'historico', label: 'Histórico', icon: History }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id as any)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-brand-600 text-brand-700' : 'border-transparent text-ink-500 hover:text-ink-800'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'novo' && <NovoAjuste onDone={() => setTab('historico')} />}
      {tab === 'historico' && <HistoricoAjustes />}
    </div>
  )
}

function NovoAjuste({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient()
  const [produto, setProduto] = useState<Produto | null>(null)
  const [busca, setBusca] = useState('')
  const [depositoId, setDepositoId] = useState<number | ''>('')
  const [modo, setModo] = useState<'saldo' | 'delta'>('saldo')
  const [novoSaldo, setNovoSaldo] = useState('')
  const [tipo, setTipo] = useState<'entrada' | 'saida'>('saida')
  const [quantidade, setQuantidade] = useState('')
  const [motivo, setMotivo] = useState('contagem')
  const [contaId, setContaId] = useState<number | ''>('')
  const [observacao, setObservacao] = useState('')

  const { data: depositos = [] } = useQuery<Deposito[]>({ queryKey: ['depositos'], queryFn: async () => (await api.get('/estoque/depositos')).data })
  const depAtivos = useMemo(() => depositos.filter(d => d.ativo), [depositos])
  useEffect(() => { if (depAtivos.length && depositoId === '') setDepositoId((depAtivos.find(d => d.is_default) || depAtivos[0]).id) }, [depAtivos, depositoId])

  const { data: contas = [] } = useQuery<ContaGerencial[]>({ queryKey: ['contas-analiticas'], queryFn: async () => (await api.get('/plano-contas/analiticas')).data })
  const contasOrd = useMemo(() => {
    const ordem: any = { custo: 0, despesa_op: 1, despesa_fin: 2, deducao: 3 }
    return [...contas].sort((a, b) => (ordem[a.tipo] ?? 99) - (ordem[b.tipo] ?? 99) || a.codigo.localeCompare(b.codigo))
  }, [contas])

  const { data: produtosBusca = [] } = useQuery<Produto[]>({
    queryKey: ['prod-busca-ajuste', busca],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q: busca } })).data,
    enabled: busca.length >= 2,
  })

  const saldoAtual = produto ? Number(produto.estoque_atual) || 0 : 0
  const delta = !produto ? 0
    : modo === 'saldo' ? (parseFloat(novoSaldo || '') - saldoAtual)
    : (tipo === 'entrada' ? 1 : -1) * (parseFloat(quantidade || '0') || 0)
  const saldoFinal = saldoAtual + (isNaN(delta) ? 0 : delta)

  const podeEnviar = produto && depositoId && motivo &&
    (modo === 'saldo' ? novoSaldo !== '' && delta !== 0 && !isNaN(delta) : (parseFloat(quantidade || '0') > 0))

  const submit = useMutation({
    mutationFn: async () => (await api.post('/estoque/ajustes', {
      produto_id: produto!.id, deposito_id: depositoId, modo,
      tipo, quantidade: parseFloat(quantidade || '0') || 0,
      novo_saldo: modo === 'saldo' ? parseFloat(novoSaldo || '0') : null,
      motivo, conta_gerencial_id: contaId || null, observacao: observacao || null,
    })).data,
    onSuccess: (r: any) => {
      toast.success(`Ajuste feito: ${r.delta > 0 ? '+' : ''}${r.delta} → saldo ${r.saldo_pos}`, { duration: 5000 })
      qc.invalidateQueries({ queryKey: ['mov-historico'] })
      setProduto(null); setBusca(''); setNovoSaldo(''); setQuantidade(''); setObservacao(''); setContaId('')
      onDone()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao ajustar'),
  })

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Produto */}
      <div className="card card-body">
        <h3 className="font-semibold mb-2">Produto</h3>
        {!produto ? (
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input className="input pl-8" placeholder="Buscar produto por código ou nome (mín. 2 letras)..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={enterAbrePrimeiro(produtosBusca, p => { setProduto(p); setBusca(''); setNovoSaldo(String(Number(p.estoque_atual) || 0)) })} />
            {busca.length >= 2 && produtosBusca.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-auto">
                {produtosBusca.map(p => (
                  <button key={p.id} onClick={() => { setProduto(p); setBusca(''); setNovoSaldo(String(Number(p.estoque_atual) || 0)) }}
                    className="w-full text-left px-3 py-2 hover:bg-ink-50 text-sm border-b last:border-0">
                    <span className="font-medium">{p.codigo}</span> · {p.descricao}
                    <span className="text-ink-400"> — saldo {Number(p.estoque_atual) || 0} {p.unidade}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between bg-ink-50 rounded-lg px-3 py-2">
            <div>
              <div className="font-medium text-sm">{produto.codigo} · {produto.descricao}</div>
              <div className="text-xs text-ink-500">Saldo atual: <strong>{saldoAtual}</strong> {produto.unidade} · custo {formatMoney(Number(produto.preco_custo) || 0)}</div>
            </div>
            <button onClick={() => setProduto(null)} className="text-ink-400 hover:text-rose-600"><X size={16} /></button>
          </div>
        )}
      </div>

      {produto && (
        <div className="card card-body space-y-4">
          {/* Depósito + modo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label flex items-center gap-1"><Warehouse size={12} /> Depósito</label>
              <select className="input" value={depositoId} onChange={e => setDepositoId(Number(e.target.value))}>
                {depAtivos.map(d => <option key={d.id} value={d.id}>{d.nome}{d.is_default ? ' ★' : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Tipo de ajuste</label>
              <div className="flex gap-1 bg-ink-50 rounded-md p-1">
                {[['saldo', 'Contagem (definir saldo)'], ['delta', 'Entrada / Saída (+/−)']].map(([v, l]) => (
                  <button key={v} onClick={() => setModo(v as any)}
                    className={`flex-1 px-2 py-1.5 text-xs rounded transition ${modo === v ? 'bg-white text-brand-700 font-medium shadow-sm' : 'text-ink-600'}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Entrada de valor conforme modo */}
          {modo === 'saldo' ? (
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <label className="label">Saldo contado (real) *</label>
                <input type="number" step="0.001" className="input text-lg" value={novoSaldo} onChange={e => setNovoSaldo(e.target.value)} />
              </div>
              <div className="text-sm">
                <span className="text-ink-500">Diferença: </span>
                <span className={`font-bold ${delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-rose-700' : 'text-ink-500'}`}>
                  {delta > 0 ? '+' : ''}{isNaN(delta) ? 0 : delta} {produto.unidade}
                </span>
                <span className="text-ink-400"> · saldo final {isNaN(saldoFinal) ? saldoAtual : saldoFinal}</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <label className="label">Movimento</label>
                <div className="flex gap-1 bg-ink-50 rounded-md p-1">
                  <button onClick={() => setTipo('entrada')} className={`flex-1 px-2 py-1.5 text-xs rounded flex items-center justify-center gap-1 ${tipo === 'entrada' ? 'bg-white text-emerald-700 font-medium shadow-sm' : 'text-ink-600'}`}><TrendingUp size={12} /> Entrada</button>
                  <button onClick={() => setTipo('saida')} className={`flex-1 px-2 py-1.5 text-xs rounded flex items-center justify-center gap-1 ${tipo === 'saida' ? 'bg-white text-rose-700 font-medium shadow-sm' : 'text-ink-600'}`}><TrendingDown size={12} /> Saída</button>
                </div>
              </div>
              <div>
                <label className="label">Quantidade *</label>
                <input type="number" step="0.001" className="input" value={quantidade} onChange={e => setQuantidade(e.target.value)} />
              </div>
              <div className="text-sm pb-2">
                <span className="text-ink-400">saldo final </span><strong>{isNaN(saldoFinal) ? saldoAtual : saldoFinal}</strong>
              </div>
            </div>
          )}

          {/* Motivo + conta gerencial */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Motivo *</label>
              <select className="input" value={motivo} onChange={e => setMotivo(e.target.value)}>
                {MOTIVOS.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label flex items-center gap-1">Conta gerencial <Lightbulb size={11} className="text-amber-500" /></label>
              <select className="input" value={contaId} onChange={e => setContaId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">— sem classificação —</option>
                {contasOrd.map(c => <option key={c.id} value={c.id}>{c.codigo} — {c.nome} ({c.tipo.replace('_', ' ')})</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="label">Observação</label>
            <input className="input" value={observacao} onChange={e => setObservacao(e.target.value)} placeholder="Ex: contagem mensal, produto avariado no transporte..." />
          </div>

          {saldoFinal < 0 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">⚠ Esse ajuste deixa o saldo negativo ({saldoFinal} {produto.unidade}). Confira a quantidade.</div>
          )}

          <div className="flex justify-end">
            <button disabled={!podeEnviar || submit.isPending} onClick={() => submit.mutate()} className="btn-primary">
              {submit.isPending ? <Loader2 size={14} className="animate-spin" /> : <SlidersHorizontal size={14} />}
              Aplicar ajuste
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function HistoricoAjustes() {
  const [dias, setDias] = useState(30)
  const [motivo, setMotivo] = useState('')
  const [tipo, setTipo] = useState('')
  const [soAjustes, setSoAjustes] = useState(true)

  const { data: movs = [], isLoading } = useQuery<any[]>({
    queryKey: ['mov-historico', dias, motivo, tipo, soAjustes],
    queryFn: async () => (await api.get('/estoque/movimentos', {
      params: { dias, motivo: motivo || undefined, tipo: tipo || undefined, origem: soAjustes ? 'ajuste' : undefined, limit: 500 },
    })).data,
  })

  return (
    <div className="space-y-3">
      <div className="card card-body flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 bg-ink-50 rounded-md p-1">
          {[[7, '7 dias'], [30, '30 dias'], [90, '90 dias'], [0, 'Tudo']].map(([v, l]) => (
            <button key={v} onClick={() => setDias(v as number)} className={`px-2 py-1 text-xs rounded ${dias === v ? 'bg-white text-brand-700 font-medium shadow-sm' : 'text-ink-600'}`}>{l}</button>
          ))}
        </div>
        <select className="input w-48 text-sm" value={motivo} onChange={e => setMotivo(e.target.value)}>
          <option value="">Todos os motivos</option>
          {MOTIVOS.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
        </select>
        <select className="input w-40 text-sm" value={tipo} onChange={e => setTipo(e.target.value)}>
          <option value="">Entrada e saída</option>
          <option value="entrada">Só entradas</option>
          <option value="saida">Só saídas</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-ink-600">
          <input type="checkbox" checked={soAjustes} onChange={e => setSoAjustes(e.target.checked)} /> Só ajustes
        </label>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr>
              {['Data', 'Produto', 'Mov.', 'Qtd', 'Saldo após', 'Motivo', 'Conta gerencial', 'Usuário', 'Obs.'].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={9} className="text-center py-8 text-ink-400">Carregando...</td></tr>}
            {!isLoading && movs.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-ink-400">Nenhum movimento nesse filtro.</td></tr>}
            {movs.map(m => (
              <tr key={m.id} className="hover:bg-ink-50">
                <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-600">{m.criado_em ? new Date(m.criado_em).toLocaleString('pt-BR') : '—'}</td>
                <td className="px-3 py-2">{m.produto_descricao || `#${m.produto_id}`}</td>
                <td className="px-3 py-2">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${m.tipo === 'entrada' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                    {m.tipo === 'entrada' ? '+ entrada' : '− saída'}
                  </span>
                </td>
                <td className="px-3 py-2 font-medium">{Number(m.quantidade)}</td>
                <td className="px-3 py-2">{Number(m.saldo_pos)}</td>
                <td className="px-3 py-2 text-xs">{m.origem === 'ajuste' ? motivoLabel(m.motivo) : <span className="text-ink-400 capitalize">{m.origem}</span>}</td>
                <td className="px-3 py-2 text-xs text-ink-600">{m.conta_gerencial_nome || '—'}</td>
                <td className="px-3 py-2 text-xs text-ink-600 whitespace-nowrap"><span className="inline-flex items-center gap-1"><User size={11} className="text-ink-400" />{m.usuario_nome || '—'}</span></td>
                <td className="px-3 py-2 text-xs text-ink-500 max-w-[180px] truncate" title={m.observacao || ''}>{m.observacao || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
